import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, EventEmitter, HostListener, inject, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { AuthService } from 'src/app/auth/auth.service';
import { Route } from '../routes';

export interface SideNavToggle {
  screenWidth: number;
  isCollapsed: boolean;
  isMobile: boolean;
}
@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('150ms', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('150ms', style({ opacity: 0 }))
            ])
        ]),
    ],
    standalone: false
})
export class SidebarComponent implements OnInit {

  private authService = inject(AuthService);
  @Output() readonly onToggleSideNav = new EventEmitter<SideNavToggle>();
  @Input() routes: Route[] = [];
  screenWidth = 0;
  isCollapsed = true;
  isMobile = false;
  faCaretDown = faCaretDown;

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {
  }

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    const user = this.authService.user
    const permissions = user?.editPermissions ? user.editPermissions?.split(',') : [];

    if (!permissions.includes('Chat Gen BAI')) {
      this.routes = this.routes.filter(route => route.name !== 'Chat Gen BAI');
    }
    this.setCollapsed();
  }

  /**
   * A function that listens for window resizes and sets the collapsed property accordingly.    
   * @returns None       
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.screenWidth !== window.innerWidth) {
      this.screenWidth = window.innerWidth;
      this.setCollapsed();
    }
  }

  /**
   * Toggles the collapsed state of the side nav.           
   * @returns None           
   */
  public toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
    this.resetCollapse();
    this.onToggleSideNav.emit({ isCollapsed: this.isCollapsed, screenWidth: this.screenWidth, isMobile: this.isMobile });
  }

  /**
   * Close the side nav.           
   * @returns None           
   */
  public closeCollapsed(): void {
    this.isCollapsed = false;
    this.onToggleSideNav.emit({ isCollapsed: this.isCollapsed, screenWidth: this.screenWidth, isMobile: this.isMobile });
  }

  /**
   * Sets the isCollapsed property to true if the screen width is less than 768 pixels.           
   * @returns None           
   */
  public setCollapsed(): void {
    if (this.screenWidth <= 768) {
      this.isCollapsed = true;
      this.isMobile = true;
      this.resetCollapse();
    } else {
      this.isCollapsed = false;
      this.isMobile = false;
    }
    this.onToggleSideNav.emit({ isCollapsed: this.isCollapsed, screenWidth: this.screenWidth, isMobile: this.isMobile });
  }

  /**
   * Resets the collapse state of the element.
   * If the element is currently collapsed, it will add the 'collapsed' class from the 'expanded' element
   * and remove the 'show' class from the 'collapse' element.
   * @returns None
   */
  public resetCollapse() {
    const expandedList = this.elementRef.nativeElement.querySelector(".sidebar-nav");
    if (this.isCollapsed && expandedList) {
      for (const child of expandedList.children) {
        const collapse = child.querySelector(".collapse");
        const expanded = child.querySelector(".expanded");
        if (collapse) {
          this.renderer.removeClass(collapse, 'show');
        }
        if (expanded) {
          this.renderer.addClass(expanded, 'collapsed');
        }
      }
    }
  }

  /**
   * Removes all spaces from the given string.
   * @param {string} name - The string to remove spaces from.
   * @returns The input string with all spaces removed.
   */
  getTrimmedName(name: string): string {
    return name.replace(/\s/g, '')
  }
}
